import type { VersionedRuntimeEntity } from './normalized-store';
import { workerMobileMovementPointSchema, workerMobileProjectionSchema, type WorkerMobileProjection } from '../worker-mobile/model';
import { safetyZoneSettingsSchema } from '../worker-mobile/safety-zones';
import type { DeviceMovementHistoryItem } from '@/shared/api/device-map-marker-client';
import { recordReadFailure } from '@/shared/logging/read-failure';

export interface FieldRolloutState {
  field_uid: string;
  active_phase: string;
  routing_version: number;
  ownership_epoch: number;
  presentation_provider: 'V2';
  can_preview_v2: boolean;
  available_providers: Array<'V2'>;
}

export interface FieldProjectionSnapshot {
  products: VersionedRuntimeEntity[];
  groups: VersionedRuntimeEntity[];
  markers: VersionedRuntimeEntity[];
  alarms: VersionedRuntimeEntity[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readEntities(value: unknown, fieldUid: string, runtimePrefix: string): VersionedRuntimeEntity[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(item => {
    const record = asRecord(item);
    if (!record) return [];
    const id = String(record.runtime_key ?? record.id ?? record.uid ?? '').trim();
    const version = Number(record.version);
    if (!id || !Number.isSafeInteger(version) || version < 0 || record.field_uid !== fieldUid) throw new Error('현장 데이터의 식별자 또는 버전이 올바르지 않습니다.');
    return [
      {
        ...record,
        runtime_key: String(record.runtime_key ?? `${runtimePrefix}:${id}`),
        field_uid: String(record.field_uid ?? fieldUid),
        version,
      } as VersionedRuntimeEntity,
    ];
  });
}

async function fetchJson(path: string, signal?: AbortSignal): Promise<unknown> {
  const timeout = AbortSignal.timeout(15000);
  const startedAt = Date.now();
  const requestId = crypto.randomUUID();
  let responseRequestId = requestId;
  try {
    const response = await fetch(path, {
      cache: 'no-store',
      credentials: 'include',
      headers: { 'X-Request-Id': requestId },
      signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    });
    responseRequestId = response.headers.get('x-request-id')?.trim() || requestId;
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
      const detail = typeof payload?.detail === 'string' ? payload.detail : 'V2 데이터를 불러오지 못했습니다.';
      recordReadFailure({ path, requestId: responseRequestId, status: response.status, durationMs: Date.now() - startedAt, reason: 'HTTP_ERROR' });
      throw Object.assign(new Error(detail), { status: response.status, requestId: responseRequestId });
    }
    return await response.json();
  } catch (error) {
    // 현장 전환·언마운트 취소와 서버가 명시한 HTTP 오류는 통신 실패로 재분류하지 않습니다.
    if (signal?.aborted || typeof asRecord(error)?.status === 'number') throw error;
    const timedOut = timeout.aborted || (error instanceof Error && error.name === 'TimeoutError');
    const networkFailure = timedOut || error instanceof TypeError;
    recordReadFailure({
      path,
      requestId: responseRequestId,
      status: networkFailure ? 0 : 502,
      durationMs: Date.now() - startedAt,
      reason: timedOut ? 'TIMEOUT' : networkFailure ? 'NETWORK_ERROR' : 'INVALID_RESPONSE',
    });
    throw Object.assign(
      new Error(
        timedOut ? '현장 데이터 조회 시간이 초과되었습니다.' : networkFailure ? '네트워크 연결을 확인하고 다시 시도해 주세요.' : '현장 데이터 응답 형식이 올바르지 않습니다.'
      ),
      {
        status: networkFailure ? 0 : 502,
        code: networkFailure ? 'NETWORK_ERROR' : 'INVALID_RESPONSE',
        requestId: responseRequestId,
      }
    );
  }
}

export async function fetchFieldRollout(fieldUid: string, signal?: AbortSignal): Promise<FieldRolloutState> {
  const payload = asRecord(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/rollout`, signal));
  if (
    !payload ||
    payload.field_uid !== fieldUid ||
    payload.active_phase !== 'V2_ACTIVE' ||
    typeof payload.routing_version !== 'number' ||
    !Number.isSafeInteger(payload.routing_version) ||
    Number(payload.routing_version) < 1 ||
    typeof payload.ownership_epoch !== 'number' ||
    !Number.isSafeInteger(payload.ownership_epoch) ||
    Number(payload.ownership_epoch) < 1 ||
    payload.presentation_provider !== 'V2' ||
    payload.can_preview_v2 !== false ||
    !Array.isArray(payload.available_providers) ||
    payload.available_providers.length !== 1 ||
    payload.available_providers[0] !== 'V2'
  ) {
    throw new Error('V2 rollout 응답 형식이 올바르지 않습니다.');
  }
  return payload as unknown as FieldRolloutState;
}

export async function fetchFieldStreamCursor(fieldUid: string, signal?: AbortSignal): Promise<string> {
  const payload = asRecord(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/stream-cursor?preview=true`, signal));
  if (payload?.field_uid !== fieldUid || typeof payload.cursor !== 'string' || !/^\d+-\d+$/.test(payload.cursor)) throw new Error('실시간 시작 위치를 확인할 수 없습니다.');
  return payload.cursor;
}

export async function fetchFieldProjectionSnapshot(fieldUid: string, signal?: AbortSignal, rollout?: FieldRolloutState): Promise<FieldProjectionSnapshot> {
  const previewQuery = 'preview=true';
  const [markerPayload, eventPayload] = await Promise.all([
    fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/markers?${previewQuery}`, signal),
    fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/events?limit=200&${previewQuery}`, signal),
  ]);
  const products: VersionedRuntimeEntity[] = [];
  let groups: VersionedRuntimeEntity[] = [];
  let cursor: string | null = null;
  const visited = new Set<string>();
  do {
    const productPayload = asRecord(
      await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/product-snapshot?limit=200&${previewQuery}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, signal)
    );
    if (
      productPayload?.field_uid !== fieldUid ||
      (rollout && (productPayload.routing_version !== rollout.routing_version || productPayload.ownership_epoch !== rollout.ownership_epoch))
    ) {
      throw new Error('조회 중 현장의 운영 버전이 변경되었습니다. 다시 조회해 주세요.');
    }
    products.push(...readEntities(productPayload?.items, fieldUid, 'product'));
    if (groups.length === 0) groups = readEntities(productPayload?.groups, fieldUid, 'group');
    cursor = typeof productPayload?.next_cursor === 'string' && productPayload.next_cursor ? productPayload.next_cursor : null;
    if (cursor && visited.has(cursor)) throw new Error('현장 데이터의 다음 페이지가 올바르지 않습니다.');
    if (cursor) visited.add(cursor);
  } while (cursor);
  const markerRecord = asRecord(markerPayload);
  const eventRecord = asRecord(eventPayload);
  return {
    products,
    groups,
    markers: readEntities(markerRecord?.items, fieldUid, 'product'),
    alarms: readEntities(eventRecord?.items, fieldUid, 'alarm'),
  };
}

/** 모바일 조회는 기존 제품·알람의 초기 표시와 별도로 처리합니다. */
export async function fetchWorkerMobileSnapshot(fieldUid: string, signal: AbortSignal, rollout: FieldRolloutState): Promise<WorkerMobileProjection[]> {
  const payload = asRecord(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/mobile-devices?preview=true`, signal));
  if (!payload || payload.field_uid !== fieldUid || !Array.isArray(payload.items)) throw new Error('모바일 기기 정보를 불러오지 못했습니다.');
  if (payload.routing_version !== rollout.routing_version || payload.ownership_epoch !== rollout.ownership_epoch)
    throw new Error('모바일 조회 중 현장 운영 버전이 변경되었습니다.');
  const devices = payload.items.map(device => workerMobileProjectionSchema.parse(device));
  if (devices.some(device => device.field_uid !== fieldUid || device.routing_version !== rollout.routing_version || device.ownership_epoch !== rollout.ownership_epoch))
    throw new Error('모바일 기기의 현장 또는 운영 버전이 일치하지 않습니다.');
  return devices;
}

export async function fetchWorkerSafetyZones(fieldUid: string, signal: AbortSignal) {
  const settings = safetyZoneSettingsSchema.parse(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/safety-zones`, signal));
  if (settings.field_uid !== fieldUid) throw new Error('안전구역의 현장 정보가 일치하지 않습니다.');
  return settings;
}

export interface V2BoardSnapshot {
  read_only: true;
  v1_fallback_widget_count: number;
  v1_fallback_widgets: Array<{ uid: string; widget_type: string; reason?: string | null }>;
  [key: string]: unknown;
}

export interface ProductDetailSnapshot {
  item: VersionedRuntimeEntity;
  routing_version: number;
  ownership_epoch: number;
}

/** 지도 상세 창은 현장에 속한 V2 제품 ID로 조회하고 다른 현장의 응답은 거부합니다. */
export async function fetchProductDetail(fieldUid: string, productId: string, signal?: AbortSignal): Promise<ProductDetailSnapshot> {
  const payload = asRecord(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/products/${encodeURIComponent(productId)}?preview=true`, signal));
  const item = asRecord(payload?.item);
  if (
    payload?.provider !== 'V2' ||
    payload.field_uid !== fieldUid ||
    !Number.isSafeInteger(payload.routing_version) ||
    Number(payload.routing_version) < 1 ||
    !Number.isSafeInteger(payload.ownership_epoch) ||
    Number(payload.ownership_epoch) < 1 ||
    item?.field_uid !== fieldUid ||
    item.runtime_key !== `product:${productId}` ||
    !Number.isSafeInteger(item.version) ||
    Number(item.version) < 0
  ) {
    throw new Error('V2 제품 상세 응답 형식이 올바르지 않습니다.');
  }
  return { item: item as VersionedRuntimeEntity, routing_version: Number(payload.routing_version), ownership_epoch: Number(payload.ownership_epoch) };
}

/** 호환 장비 UID는 같은 현장의 단일 센서 연결로 확인한 뒤 제품 상세를 조회합니다. */
export async function fetchDeviceProductDetail(fieldUid: string, deviceUid: string, signal?: AbortSignal): Promise<ProductDetailSnapshot> {
  const query = new URLSearchParams({ field_uid: fieldUid, device_uid: deviceUid, preview: 'true' });
  const payload = await fetchJson(`/api/v2/sensors/latest?${query}`, signal);
  const sensor = Array.isArray(payload) && payload.length === 1 ? asRecord(payload[0]) : null;
  if (
    sensor?.field_uid !== fieldUid ||
    (sensor.device_uid !== deviceUid && sensor.sensor_id !== deviceUid) ||
    typeof sensor.sensor_id !== 'string' ||
    typeof sensor.product_id !== 'string' ||
    !sensor.product_id
  ) {
    throw new Error('해제할 장비의 원본 연결을 확인하지 못했습니다. 다시 조회해 주세요.');
  }
  const detail = await fetchProductDetail(fieldUid, sensor.product_id, signal);
  const matches = Array.isArray(detail.item.sensors)
    ? detail.item.sensors
        .map(asRecord)
        .filter(candidate => candidate !== null && candidate.id === sensor.sensor_id && (candidate.id === deviceUid || candidate.source_device_uid === deviceUid))
    : [];
  if (matches.length !== 1) throw new Error('장비의 원본 연결이 변경되었습니다. 다시 조회해 주세요.');
  return detail;
}

export async function fetchFieldV2Board(fieldUid: string, screenId: string, signal?: AbortSignal): Promise<V2BoardSnapshot> {
  const payload = asRecord(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/boards?preview=true&screen_id=${encodeURIComponent(screenId)}`, signal));
  if (!payload || payload.read_only !== true || !Array.isArray(payload.widgets)) {
    throw new Error('V2 시험 보드 응답 형식이 올바르지 않습니다.');
  }
  return payload as V2BoardSnapshot;
}

export async function fetchProductMovement(
  fieldUid: string,
  productId: string,
  options: { startAt: string; endAt: string; limit?: number; cursor?: string; kind?: 'products' | 'groups' },
  signal?: AbortSignal
): Promise<{ items: Array<{ event_id: string; sensor_id: string; observed_at: string; latitude: number; longitude: number }>; truncated: boolean; next_cursor: string | null }> {
  const query = new URLSearchParams({
    preview: 'true',
    start_at: options.startAt,
    end_at: options.endAt,
    limit: String(options.limit ?? 1000),
  });
  if (options.cursor) query.set('cursor', options.cursor);
  const payload = asRecord(
    await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/${options.kind ?? 'products'}/${encodeURIComponent(productId)}/movement?${query}`, signal)
  );
  if (!payload || payload.field_uid !== fieldUid || !Array.isArray(payload.items)) throw new Error('V2 이동 이력 응답 형식이 올바르지 않습니다.');
  const items = payload.items.flatMap(item => {
    const record = asRecord(item);
    if (
      !record ||
      typeof record.event_id !== 'string' ||
      typeof record.sensor_id !== 'string' ||
      typeof record.observed_at !== 'string' ||
      typeof record.latitude !== 'number' ||
      typeof record.longitude !== 'number'
    ) {
      throw new Error('V2 이동 이력에 올바르지 않은 행이 있습니다.');
    }
    return [record as { event_id: string; sensor_id: string; observed_at: string; latitude: number; longitude: number }];
  });
  return { items, truncated: payload.truncated === true, next_cursor: typeof payload.next_cursor === 'string' ? payload.next_cursor : null };
}

/** 사용자·앱 설치의 이력만 조회하며 제품/sensor ID를 만들어 사용하지 않습니다. */
export async function fetchAllWorkerMobileMovement(
  fieldUid: string,
  deviceUid: string,
  userUid: string,
  options: { startAt: string; endAt: string; maxPoints?: number },
  signal?: AbortSignal
): Promise<{ items: DeviceMovementHistoryItem[]; total_items: number }> {
  const items: DeviceMovementHistoryItem[] = [];
  const cursors = new Set<string>();
  const maxPoints = options.maxPoints;
  if (maxPoints !== undefined && (!Number.isSafeInteger(maxPoints) || maxPoints < 2 || maxPoints > 100000)) throw new Error('표시 좌표 수를 확인해 주세요.');
  const requestSignal = signal ? AbortSignal.any([signal, AbortSignal.timeout(120_000)]) : AbortSignal.timeout(120_000);
  let cursor: string | undefined;
  let pages = 0;
  do {
    if (++pages > 200) throw new Error('모바일 이동 이력 조회 기간을 줄여 주세요.');
    const query = new URLSearchParams({ start_at: options.startAt, end_at: options.endAt, user_uid: userUid, limit: '5000' });
    if (cursor) query.set('cursor', cursor);
    const payload = asRecord(await fetchJson(`/api/v2/fields/${encodeURIComponent(fieldUid)}/mobile-devices/${encodeURIComponent(deviceUid)}/movement?${query}`, requestSignal));
    if (!payload || payload.field_uid !== fieldUid || payload.device_uid !== deviceUid || !Array.isArray(payload.items))
      throw new Error('모바일 이동 이력 응답이 올바르지 않습니다.');
    for (const record of payload.items) {
      const point = workerMobileMovementPointSchema.parse(record);
      if (point.field_uid !== fieldUid || point.device_uid !== deviceUid || point.user_uid !== userUid) throw new Error('모바일 이동 이력의 사용자·기기가 일치하지 않습니다.');
      items.push({
        uid: point.event_id,
        field_uid: fieldUid,
        device_uid: deviceUid,
        subject_type: 'MOBILE_DEVICE',
        subject_uid: userUid,
        lat: point.latitude,
        lon: point.longitude,
        recorded_at: point.observed_at,
        movement_status: 'RECORDED',
        source: 'WORKER_MOBILE',
        is_escape_prevention_enabled: false,
        escape_alert_active: false,
      });
    }
    if (items.length > 100000) throw new Error('모바일 이동 이력은 100,000건 이내로 조회해 주세요.');
    cursor = typeof payload.next_cursor === 'string' && payload.next_cursor ? payload.next_cursor : undefined;
    if (payload.truncated && !cursor) throw new Error('모바일 이동 이력의 다음 페이지 정보가 없습니다.');
    if (cursor && cursors.has(cursor)) throw new Error('모바일 이동 이력 페이지가 반복됐습니다.');
    if (cursor) cursors.add(cursor);
  } while (cursor);
  const displayed =
    maxPoints !== undefined && items.length > maxPoints
      ? Array.from({ length: maxPoints }, (_, index) => items[Math.round((index * (items.length - 1)) / (maxPoints - 1))])
      : items;
  return { items: displayed, total_items: items.length };
}

/** 전체 다운로드는 페이지를 끝까지 읽고 상한 초과/잘못된 커서를 명시적으로 알립니다. */
export async function fetchAllProductMovement(
  fieldUid: string,
  productId: string,
  options: { startAt: string; endAt: string; limit?: number; maxPoints?: number; kind?: 'products' | 'groups' },
  signal?: AbortSignal
) {
  const items: Awaited<ReturnType<typeof fetchProductMovement>>['items'] = [];
  const cursors = new Set<string>();
  const maxPoints = options.maxPoints;
  if (maxPoints !== undefined && (!Number.isSafeInteger(maxPoints) || maxPoints < 2 || maxPoints > 100000)) throw new Error('표시 좌표 수를 확인해 주세요.');
  const requestSignal = signal ? AbortSignal.any([signal, AbortSignal.timeout(120_000)]) : AbortSignal.timeout(120_000);
  let pages = 0;
  let cursor: string | undefined;
  do {
    if (++pages > 200) throw new Error('이동 이력 페이지 조회가 너무 많습니다. 기간을 줄여 주세요.');
    const page = await fetchProductMovement(fieldUid, productId, { ...options, limit: 5000, cursor }, requestSignal);
    items.push(...page.items);
    if (items.length > 100000) throw new Error('이동 이력은 최대 100,000건까지 조회할 수 있습니다. 기간을 줄여 주세요.');
    if (page.truncated && !page.next_cursor) throw new Error('이동 이력의 다음 페이지 정보가 없습니다. 서버 적용 상태를 확인해 주세요.');
    cursor = page.next_cursor ?? undefined;
    if (cursor && cursors.has(cursor)) throw new Error('이동 이력 페이지가 반복되었습니다. 다시 조회해 주세요.');
    if (cursor) cursors.add(cursor);
  } while (cursor);
  // 지도는 처음·마지막 좌표를 포함해 균등 간격으로 표시하고, 다운로드는 전체 행을 유지합니다.
  const displayed =
    maxPoints !== undefined && items.length > maxPoints
      ? Array.from({ length: maxPoints }, (_, index) => items[Math.round((index * (items.length - 1)) / (maxPoints - 1))])
      : items;
  return { items: displayed, total_items: items.length };
}
